import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AccountInfo } from '@azure/msal-browser';
import { ApiResponse } from 'app/models/responses/api-response.model';
import { User } from 'app/core/user/user.types';
import { environment } from 'environments/environment';
import { catchError, map, Observable, of, ReplaySubject, switchMap, tap } from 'rxjs';
import { FileService } from 'app/shared/services/file.service';

@Injectable({ providedIn: 'root' })
export class UserService {
    private readonly _httpClient = inject(HttpClient);
    private readonly fileService = inject(FileService);
    private readonly _user: ReplaySubject<User> = new ReplaySubject<User>(1);
    private readonly baseUrl = `${environment.apiBaseUrl}/User`;
    private readonly graphUrl = `${environment.microsoftGraphBaseUrl}/photo/$value`;

    set user(value: User) {
        this._user.next(value);
    }

    get user$(): Observable<User> {
        return this._user.asObservable();
    }

    update(user: User): Observable<User> {
        this._user.next(user);
        return of(user);
    }

    private checkUserEmail(email: string): Observable<User | null> {
        return this._httpClient
            .get<ApiResponse<User>>(`${this.baseUrl}/checkUserEmail`, {
                params: { email },
            })
            .pipe(
                map((response) => {
                    if (response.isError) {
                        throw response.firstError ?? new Error('Failed to validate user email.');
                    }
                    return response.value ?? null;
                })
            );
    }

    initializeUserSessionFromAccount(account: AccountInfo): Observable<User | null> {
        return this.checkUserEmail(account.username).pipe(
            map((dbUser) => (dbUser ? this.buildAuthenticatedUser(account, dbUser) : null)),
            tap((user) => {
                if (!user) {
                    return;
                }

                this.update(user);
                this.saveUserInfo(user);
            }),
            switchMap((user) => {
                if (!user) {
                    return of(null);
                }

                return this.fetchAndUpdateUserInfo(user).pipe(
                    catchError((error) => {
                        console.warn('Could not fetch user photo from Microsoft Graph:', error);
                        return of(user);
                    })
                );
            })
        );
    }

    private fetchAndUpdateUserInfo(user: User): Observable<User> {
        return this.fetchUserPhoto().pipe(
            map((avatar) => ({ ...user, avatar })),
            tap((updatedUser) => {
                this.update(updatedUser);
                this.saveUserInfo(updatedUser);
            })
        );
    }

    private fetchUserPhoto(): Observable<string> {
        return this._httpClient
            .get(this.graphUrl, { responseType: 'blob' })
            .pipe(switchMap((blob) => this.fileService.blobToBase64$(blob)));
    }

    private buildAuthenticatedUser(account: AccountInfo, dbUser: User): User {
        return {
            ...dbUser,
            name: account.name ?? dbUser.name ?? dbUser.displayName ?? account.username,
            email: account.username,
            status: 'online',
        };
    }

    private saveUserInfo(user: User): void {
        localStorage.setItem('currentUser', JSON.stringify(user));

        if (user.id) {
            localStorage.setItem('userId', user.id);
        } else {
            localStorage.removeItem('userId');
        }

        if (typeof user.branchId === 'number') {
            localStorage.setItem('branchId', user.branchId.toString());
        } else {
            localStorage.removeItem('branchId');
        }

        if (user.roleId) {
            localStorage.setItem('roleId', user.roleId);
        } else {
            localStorage.removeItem('roleId');
        }

        if (user.defaultCurrency) {
            localStorage.setItem('defaultCurrency', user.defaultCurrency);
        } else {
            localStorage.removeItem('defaultCurrency');
        }

        localStorage.setItem('branchIds', JSON.stringify(user.branchIds));
    }
}
